<template>
  <div class="modal modal-historico" v-bind:class="{ 'is-active': show }">
    <div class="modal-background"></div>
    <div class="modal-card" style="width: 70% !important; height: 81% !important;">
      <header class="modal-card-head hero">
        <div class="columns" style="width: 100%">
          <div class="column is-10">
            <h1 class="title">Histórico de comparación</h1>
          </div>
          <div class="column is-2" style="text-align: end">
            <button aria-label="close" class="delete" @click="close"></button>
          </div>
        </div>
      </header>
      <section class="modal-card-body">
        <!-- Proceso ancla: el histórico siempre se cuelga del proceso desde el que
             se abrió el modal. -->
        <div v-if="hasProcess" class="has-text-centered">
          <span class="is-size-7 has-text-grey is-uppercase">Proceso actual</span>
          <h1 class="has-text-dark has-text-weight-bold is-size-5">
            {{ process.nombre }}
          </h1>
          <span class="has-text-grey is-size-7">
            Creado el {{ dateProcess(process.created) }}
          </span>
        </div>

        <!-- Previsualización de la línea de tiempo: hace visible el orden por fecha
             en vez de sólo enunciarlo. Es la misma lectura que tendrá Resultados. -->
        <div class="tira">
          <div class="tira-chips">
            <span class="tira-label">Evolución</span>
            <template v-for="(p, i) in timeline">
              <span :key="'l' + p._id" v-if="i > 0" class="tira-line"></span>
              <span
                :key="p._id"
                class="tira-chip"
                :class="{ 'is-anchor': p._id === process._id }"
                :title="p.nombre"
              >
                {{ dateProcess(p.created) }}
                <span v-if="p._id === process._id" class="tira-chip-tag">
                  actual
                </span>
              </span>
            </template>
          </div>
          <span class="has-text-grey is-size-7">
            {{ countLabel }}
          </span>
        </div>

        <!-- Estado vacío: el proceso más antiguo no tiene nada anterior que comparar. -->
        <div v-if="!hasCandidates && !search" class="notification is-light vacio">
          <span class="icon has-text-grey">
            <i class="material-icons">history_toggle_off</i>
          </span>
          <div>
            <p class="has-text-weight-bold">No hay procesos anteriores</p>
            <p class="is-size-7 has-text-grey">
              Al histórico sólo se pueden añadir procesos creados antes que
              <span class="has-text-weight-bold">{{ process.nombre }}</span>.
              Si quieres comparar contra un proceso posterior, abre el histórico
              desde ese proceso.
            </p>
          </div>
        </div>

        <template v-else>
          <h1 class="is-size-6 instruccion">
            <span class="has-text-weight-bold">Selecciona los procesos</span>
            que forman el histórico. El sistema los ordenará por fecha
            automáticamente.
          </h1>

          <div class="controles">
            <p class="control has-icons-left is-expanded">
              <input
                class="input is-focused"
                type="text"
                v-model="search"
                placeholder="Filtrar "
              />
              <span class="icon is-medium is-left">
                <i class="material-icons">search</i>
              </span>
            </p>
            <div class="buttons has-addons atajos">
              <button
                class="button is-small"
                :disabled="candidates.length <= 3"
                @click="selectLast(3)"
              >
                Últimos 3
              </button>
              <button
                class="button is-small"
                :disabled="!candidates.length"
                @click="selectAll"
              >
                Todos
              </button>
              <button
                class="button is-small"
                :disabled="!selected.length"
                @click="selectNone"
              >
                Ninguno
              </button>
            </div>
          </div>

          <div class="is-block scroll-historico is-white">
            <table class="table is-fullwidth">
              <thead class="head-stick-historico">
                <tr>
                  <th style="width: 5%">{{ filteredItems.length }}</th>
                  <th style="width: 6%">✓</th>
                  <th>Proceso</th>
                  <th style="width: 20%">Creado</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(k, v) in filteredItems"
                  :key="k._id"
                  @click="toggle(k._id)"
                  class="fila-historico"
                >
                  <td>{{ v + 1 }}</td>
                  <td @click.stop>
                    <input
                      class="is-checkradio is-dark"
                      type="checkbox"
                      :id="'hist-' + k._id"
                      :value="k._id"
                      v-model="selected"
                    />
                    <label :for="'hist-' + k._id"></label>
                  </td>
                  <td>{{ k.nombre }}</td>
                  <td>{{ dateProcess(k.created) }}</td>
                </tr>
                <tr v-if="!filteredItems.length">
                  <td colspan="4" class="has-text-centered has-text-grey">
                    Ningún proceso coincide con el filtro.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>
      <footer class="modal-card-foot">
        <div class="buttons">
          <button
            v-if="hasHistoric"
            class="button is-danger"
            @click="deleteComp"
          >
            Eliminar histórico
          </button>
          <button class="button" @click="close">Cerrar</button>
          <button class="button is-dark" id="btnsave" @click="save">
            Guardar<span v-if="selected.length"> ({{ selected.length }})</span>
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
// Un histórico es la evolución *hasta* el proceso ancla, así que sólo son
// candidatos los procesos anteriores. Esto además hace imposible un ciclo.
const filterItems = (query, data, process) => {
  const anchorDate = Number(new Date(process.created));
  return data
    .filter((el) => {
      if (el._id === process._id) return false;
      if (Number(new Date(el.created)) >= anchorDate) return false;
      return el.nombre.toLowerCase().indexOf(query.toLowerCase()) > -1;
    })
    .sort(
      (objA, objB) =>
        Number(new Date(objB.created)) - Number(new Date(objA.created)),
    );
};

export default {
  props: {
    show: Boolean,
    process: Object,
    listProcesses: Array,
  },
  data() {
    return {
      search: "",
      selected: [],
    };
  },
  computed: {
    hasProcess() {
      return this.process && Object.keys(this.process).length > 0;
    },
    // Todos los candidatos válidos, sin aplicar el filtro de texto. Los atajos
    // (Todos / Últimos 3) operan sobre esto para que no dependan del filtro.
    candidates() {
      if (!this.hasProcess) return [];
      return filterItems("", this.listProcesses || [], this.process);
    },
    hasCandidates() {
      return this.candidates.length > 0;
    },
    filteredItems() {
      if (!this.hasProcess) return [];
      return filterItems(this.search, this.listProcesses || [], this.process);
    },
    // Ancla + seleccionados, de más antiguo a más reciente: el orden real que
    // tendrá el histórico al guardarse.
    timeline() {
      if (!this.hasProcess) return [];
      const chosen = this.candidates.filter(
        (p) => this.selected.indexOf(p._id) > -1,
      );
      return [...chosen, this.process].sort(
        (objA, objB) =>
          Number(new Date(objA.created)) - Number(new Date(objB.created)),
      );
    },
    countLabel() {
      const n = this.selected.length;
      if (!n) return "Sin procesos en el histórico";
      if (n === 1) return "1 proceso en el histórico";
      return `${n} procesos en el histórico`;
    },
    hasHistoric() {
      if (!this.hasProcess) return false;
      if (this.process.historic && this.process.historic.length > 0) return true;
      return !!this.process.comparison;
    },
  },
  methods: {
    close() {
      this.search = "";
      this.selected = [];
      this.$emit("closeModalComp");
    },
    toggle(id) {
      const i = this.selected.indexOf(id);
      if (i > -1) this.selected.splice(i, 1);
      else this.selected.push(id);
    },
    selectAll() {
      this.selected = this.candidates.map((p) => p._id);
    },
    selectLast(n) {
      // `candidates` viene ordenado del más reciente al más antiguo.
      this.selected = this.candidates.slice(0, n).map((p) => p._id);
    },
    selectNone() {
      this.selected = [];
    },
    save() {
      try {
        if (!this.selected.length) {
          this.$store.dispatch("notify", {
            message: `Debes elegir al menos un proceso para el histórico`,
            type: "is-danger",
          });
          return;
        }
        this.$emit("saveHistoric", this.process, this.selected);
      } catch (err) {
        console.log(err);
        this.$store.dispatch("notify", {
          message: `Hubo un problema al guardar el histórico`,
          type: "is-danger",
        });
      }
    },
    async deleteComp() {
      await this.$emit("deleteComparison", this.process._id);
    },
    dateProcess(d) {
      let date = new Date(d);
      const options = { year: "numeric", month: "numeric", day: "numeric" };
      date = date.toLocaleDateString(undefined, options);
      return date;
    },
  },
  watch: {
    show(nv) {
      if (!nv) return;
      this.search = "";
      // Precarga: `historic` es la fuente nueva; `comparison` cubre los procesos
      // guardados antes de que existiera el histórico.
      if (this.process.historic && this.process.historic.length > 0) {
        this.selected = this.process.historic.map((p) =>
          typeof p === "object" ? p._id : p,
        );
      } else if (this.process.comparison) {
        const c = this.process.comparison;
        this.selected = [typeof c === "object" ? c._id : c];
      } else {
        this.selected = [];
      }
    },
  },
};
</script>

<style scoped>
.modal-historico .modal-card-body > * + * {
  margin-top: 22px;
}

/* Línea de tiempo */
.tira {
  background: #f6f6f8;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.tira-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.tira-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b6b76;
  margin-right: 4px;
}

.tira-line {
  width: 14px;
  height: 1px;
  background: #d6d6de;
}

.tira-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d6d6de;
  border-radius: 20px;
  background: #fff;
  color: #6b6b76;
  font-size: 11px;
  padding: 5px 11px;
  white-space: nowrap;
}

.tira-chip.is-anchor {
  background: #510087;
  border-color: #510087;
  color: #fff;
  font-weight: 600;
}

.tira-chip-tag {
  color: #ffffffb0;
  font-size: 10px;
  font-weight: normal;
}

/* Controles */
.controles {
  display: flex;
  align-items: center;
  gap: 14px;
}

.controles .control {
  flex: 1 1 auto;
  margin-bottom: 0;
}

.atajos {
  flex: 0 0 auto;
  margin-bottom: 0 !important;
}

.atajos .button {
  margin-bottom: 0 !important;
}

.instruccion {
  color: #363636;
}

/* Tabla: definida aquí para no depender de que otro componente filtre
   `.scroll-table` al ámbito global. */
.scroll-historico {
  height: 210px;
  overflow-y: auto;
  overflow-x: hidden;
}

.head-stick-historico th {
  background: white;
  position: sticky;
  top: -3px;
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
  z-index: 60 !important;
}

.fila-historico {
  cursor: pointer;
}

.fila-historico:hover {
  background: #faf7fd;
}

/* El label del checkradio necesita un ancho mínimo para ser clicable. */
.fila-historico .is-checkradio[type="checkbox"] + label {
  padding-left: 1.5rem;
  margin-right: 0;
  min-height: 1.5rem;
}

.vacio {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/climan/components/ModalComparacionHistorico.vue